import GlobalElementsPage from "@iewc/Components/GlobalElementsPage";
import { setGA4SapNumberEvent } from "@iewc/Store/Handlers/Utilities/CustomTracking";
import { trackPageChange } from "@iewc/Utilities/tracking";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page, { PageProps } from "@insite/mobius/Page";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { ReactNode } from "react";
import { connect } from "react-redux";
import styled from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { session } = state.context;

    const currentBillTo = getBillToState(state, session.billToId).value;
    const customerNumber = session.billToId == null ? "54080" : currentBillTo?.customerNumber?.replace(/^0+/, "");

    return {
        customerNumber,
        currentPageType: getCurrentPage(state).type,
        location: getLocation(state),
        page: getCurrentPage(state),
    };
};

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
    showSignInOverlay?: boolean;
    signInOverlay?: ReactNode;
    showStorePickupOverlay?: boolean;
    storePickupOverlay?: ReactNode;
    showAddressBookOverlay?: boolean;
    addressBookOverlay?: ReactNode;
}

type Props = PageLayoutProps & ReturnType<typeof mapStateToProps>;

// This doesn't currently support HMR
const PageLayout = ({
    showHeader,
    header,
    showBreadcrumbs,
    pageContent,
    showFooter,
    footer,
    showCompactHeader,
    compactHeader,
    showSignInOverlay,
    signInOverlay,
    showStorePickupOverlay,
    storePickupOverlay,
    showAddressBookOverlay,
    addressBookOverlay,

    /* Verndale Custom Feature - New variables */
    customerNumber,
    currentPageType,
    location,
    page,
}: Props) => {
    /* Verndale Custom Feature - Add style for wrapper page */
    const wrapperStyle: InjectableCss = {};

    React.useEffect(() => {
        if (customerNumber && currentPageType !== "NullPage") {
            setGA4SapNumberEvent(customerNumber);
        }
    }, [customerNumber, currentPageType]);

    React.useEffect(() => {
        const url = location.pathname + location.search;
        const segments = url.split("/").filter(Boolean);
        const firstSegment = segments[0];
        const secondSegment = segments[1];
        const isCategory = (firstSegment === "wire-and-cable" || firstSegment === "wire-management") && !secondSegment;
        const isSubcategory = (firstSegment === "wire-and-cable" || firstSegment === "wire-management") && secondSegment; 

        if (isCategory || isSubcategory) {
            trackPageChange("CategoryDetailsPage");
        }
    }, [location]);

    return (
        <PageLayoutWrapper>
            {showHeader && header}
            {showCompactHeader && compactHeader}
            {/* Verndale Custom Feature - Wrap content editor pages to show global components / elements */}
            <StyledWrapper {...wrapperStyle}>
                {showBreadcrumbs && (
                    <Page as="div">
                        <PageBreadcrumbs />
                    </Page>
                )}
                {pageContent}
                <GlobalElementsPage />
            </StyledWrapper>
            {showFooter && footer}
            {showSignInOverlay && signInOverlay}
            {showStorePickupOverlay && storePickupOverlay}
            {showAddressBookOverlay && addressBookOverlay}
            <VisuallyHidden>
                <Typography id="tst_userNav_customerNumber">{customerNumber}</Typography>
            </VisuallyHidden>
        </PageLayoutWrapper>
    );
};

const PageLayoutWrapper = styled.div`
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    > main {
        flex: 1 1 0%;
    }

    @media print {
        display: block;
    }
`;

export default connect(mapStateToProps)(PageLayout as any);
